/**
 * MixedActionTable.helpers — memoized row components for MixedActionTable.
 *
 * Built on the shared `Table` / `Table.Row` / `Table.Cell` primitives from
 * `@/lib/base/table` so the mixed-action table stays visually consistent
 * with the rest of the app's data tables. Action-specific wiring (chevron,
 * status icon, action renderer, property/association value formatting,
 * per-row edit toggle) lives here.
 */

import { ChevronDown, ChevronRight } from '@untitledui/icons';
import type React from 'react';
import { memo, useCallback } from 'react';

import { Badge } from '@/lib/base/badges/badges';
import { CheckboxBase } from '@/lib/base/forms/checkbox';
import { Table } from '@/lib/base/table';
import { renderStatusIcon } from '@/lib/proposals/utils/actionStatus';
import { isActionAlreadyExecuted } from '@/lib/proposals/utils/partialExecution';

import type { ProposedAction } from '../../lib/proposals/core/types';
import { useActionDisplayNumber } from '../../lib/proposals/hooks/ActionDisplayNumberContext';
import type { SelectionState } from '../../lib/proposals/types/selection';
import {
  getAlreadyExecutedBadgeText,
  getSkippedBadgeText,
} from '../../lib/proposals/utils/actionWarnings';
import { getStableActionKey } from '../../lib/proposals/utils/helpers';
import { ActionRenderer } from './ActionRenderer';
import { CheckboxCell } from './CheckboxCell';
import { EditActionToggle } from './EditActionToggle';
import {
  CHROME_CELL_CLASSES,
  EDIT_CELL_CLASSES,
  EDIT_HEAD_CLASSES,
  MEDIUM_CELL_CLASSES,
} from './MixedActionTable.cellClasses';

// Re-exported so existing callers importing `HomogeneousDataRow` from
// './MixedActionTable.helpers' (the module's established public path) are
// unaffected by the homogeneous row living in its own file now. (No existing
// caller imports the CHROME_CELL_CLASSES/MEDIUM_CELL_CLASSES constants from
// this module path, so those aren't re-exported here.)
export type { HomogeneousDataRowProps } from './MixedActionTable.homogeneousRow';
export { HomogeneousDataRow } from './MixedActionTable.homogeneousRow';

export interface MixedActionTableHeaderRowProps {
  isHomogeneous: boolean;
  selectionState?: SelectionState;
  editColumnEnabled: boolean;
  onHeaderCheckboxChange: () => void;
}

/**
 * The table's `<Table.Header>` row: chrome columns (chevron, optional
 * selection checkbox, status) are identical across homogeneous and mixed
 * modes; only the data columns (Name/Property/Current/Proposed vs. a
 * single Action column) differ.
 */
export function MixedActionTableHeaderRow({
  isHomogeneous,
  selectionState,
  editColumnEnabled,
  onHeaderCheckboxChange,
}: MixedActionTableHeaderRowProps) {
  const handleHeaderCheckboxClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onHeaderCheckboxChange();
    },
    [onHeaderCheckboxChange]
  );

  const handleHeaderCheckboxKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.stopPropagation();
        e.preventDefault();
        onHeaderCheckboxChange();
      }
    },
    [onHeaderCheckboxChange]
  );

  return (
    <Table.Header className='bg-primary'>
      <Table.Head id='expand' className={CHROME_CELL_CLASSES} />
      {selectionState && (
        <Table.Head id='checkbox' className={CHROME_CELL_CLASSES}>
          <button
            type='button'
            onClick={handleHeaderCheckboxClick}
            onKeyDown={handleHeaderCheckboxKeyDown}
            aria-label='Select all actions'
            className='flex items-center'
          >
            <CheckboxBase
              isSelected={selectionState.isAllSelected}
              isIndeterminate={
                selectionState.isSomeSelected && !selectionState.isAllSelected
              }
            />
          </button>
        </Table.Head>
      )}
      <Table.Head id='status' className={CHROME_CELL_CLASSES} />
      {isHomogeneous ? (
        <>
          <Table.Head id='name' label='Name' isRowHeader />
          <Table.Head
            id='property'
            label='Property'
            className={MEDIUM_CELL_CLASSES}
          />
          <Table.Head
            id='current'
            label='Current'
            className={MEDIUM_CELL_CLASSES}
          />
          <Table.Head
            id='proposed'
            label='Proposed'
            className={MEDIUM_CELL_CLASSES}
          />
        </>
      ) : (
        <Table.Head id='action' label='Action' isRowHeader />
      )}
      {editColumnEnabled && (
        <Table.Head id='edit' aria-label='Edit' className={EDIT_HEAD_CLASSES} />
      )}
    </Table.Header>
  );
}

export interface MixedActionDataRowProps {
  action: ProposedAction;
  isSelected: boolean;
  isDisabled: boolean;
  /** Why the row is disabled, shown as a tooltip on its checkbox. */
  disabledReason: string | null;
  isExpanded: boolean;
  onToggle: (actionId: number) => void;
  onRowClick: (actionId: number) => void;
  selectionEnabled: boolean;
  orgId: number;
  /** Precomputed count of absorbed association actions for "+N links" badge */
  associationCount: number;
  /**
   * Whether the edit column should be rendered for this table. All rows in a
   * table either render an edit cell (possibly empty) or none of them do, so
   * column widths stay aligned.
   */
  editColumnEnabled: boolean;
  /**
   * Whether this specific action supports editing. When false but
   * `editColumnEnabled` is true, an empty placeholder cell is rendered so the
   * row still lines up with the header.
   */
  supportsEditing: boolean;
  /**
   * Click handler invoked when the per-row Edit button is activated. The
   * parent is expected to expand the row *and* flag it for auto-edit so the
   * expanded row enters edit mode on mount.
   */
  onEditClick: (actionId: number) => void;
}

export const MixedActionDataRow = memo(function MixedActionDataRow({
  action,
  isSelected,
  isDisabled,
  disabledReason,
  isExpanded,
  onToggle,
  onRowClick,
  selectionEnabled,
  orgId,
  associationCount,
  editColumnEnabled,
  supportsEditing,
  onEditClick,
}: MixedActionDataRowProps) {
  // Row position in the rendered list — what "Select action N" must say.
  const displayNumber = useActionDisplayNumber(action.id);

  const isAlreadyRan = isActionAlreadyExecuted(action);

  const handleAction = useCallback(
    () => onRowClick(getStableActionKey(action)),
    [onRowClick, action]
  );
  const handleEditToggle = useCallback(
    () => onEditClick(getStableActionKey(action)),
    [onEditClick, action]
  );
  const handleEditCellClick = useCallback((event: React.MouseEvent) => {
    // Prevent the parent row's onAction from firing — the Edit button owns
    // the expand+edit flow end-to-end.
    event.stopPropagation();
  }, []);
  const handleEditCellKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.stopPropagation();
    }
  }, []);
  const handleRowKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      // React Aria fires onAction for Enter/click but not for Space (which is
      // reserved for selection). Manually trigger the expand handler on Space
      // so keyboard-only users can toggle row expansion the same way as Enter.
      if (event.key === ' ') {
        event.preventDefault();
        handleAction();
      }
    },
    [handleAction]
  );
  return (
    // react-aria treats `id` as the collection key and THROWS ("Cannot change
    // the id of an item") if it moves on an existing node — so it must be the
    // version-stable identity, matching the React key on the row.
    // `data-action-id` intentionally stays the real row id: it identifies the
    // row currently on screen, which is what API calls and DOM lookups want.
    <Table.Row
      id={String(getStableActionKey(action))}
      data-action-id={action.id}
      className={`cursor-pointer ${!isSelected ? 'opacity-50 grayscale' : ''} ${isExpanded ? 'bg-secondary' : ''}`}
      onAction={handleAction}
      onKeyDown={handleRowKeyDown}
    >
      <Table.Cell className={CHROME_CELL_CLASSES}>
        {isExpanded ? (
          <ChevronDown className='size-4 text-quaternary' />
        ) : (
          <ChevronRight className='size-4 text-quaternary' />
        )}
      </Table.Cell>
      {selectionEnabled && (
        <Table.Cell className={CHROME_CELL_CLASSES}>
          <CheckboxCell
            isSelected={isSelected}
            isDisabled={isDisabled}
            disabledReason={disabledReason}
            onToggle={onToggle}
            actionId={action.id}
            executionOrder={displayNumber}
            tooltip={
              action.verdict?.status === 'not_visible'
                ? "Some of this action's details aren't visible to you."
                : undefined
            }
          />
        </Table.Cell>
      )}
      <Table.Cell className={CHROME_CELL_CLASSES}>
        {renderStatusIcon(action)}
      </Table.Cell>
      <Table.Cell>
        <div className='flex items-center gap-2 min-w-0'>
          <ActionRenderer action={action} mode='compact' orgId={orgId} />
          {associationCount > 0 && (
            <Badge color='gray' size='sm'>
              +{associationCount} {associationCount === 1 ? 'link' : 'links'}
            </Badge>
          )}
          {selectionEnabled && isAlreadyRan && (
            <Badge color='gray' size='sm'>
              {getAlreadyExecutedBadgeText(action)}
            </Badge>
          )}
          {selectionEnabled && !isSelected && !isAlreadyRan && (
            <Badge color='gray' size='sm'>
              {getSkippedBadgeText(action)}
            </Badge>
          )}
        </div>
      </Table.Cell>
      {editColumnEnabled && (
        <Table.Cell
          className={EDIT_CELL_CLASSES}
          onClick={handleEditCellClick}
          onKeyDown={handleEditCellKeyDown}
        >
          {supportsEditing && !isExpanded && (
            <EditActionToggle isEditing={false} onToggle={handleEditToggle} />
          )}
        </Table.Cell>
      )}
    </Table.Row>
  );
});
